// ============================================================================
// useTheme — the ONE definition of "which theme am I in".
//
// CW-J Phase 2 (2026-08-11). Ben's gate decision, approved 2026-08-11 before
// any code was written: a THREE-WAY System / Light / Dark control in the avatar
// menu, defaulting to Light.
//
// Three-way, not a toggle, because "System" and "Light" are different
// statements. A toggle cannot express "match my laptop" — it can only express
// a fixed choice, so a user who wants their OS setting honoured has no way to
// say so, and a user who wants Light regardless has no way to stop the OS
// overriding them at sunset.
//
// HOW IT APPLIES: one class on <html>. There are no `dark:` variants anywhere
// in src/ — every colour in the app already resolves through a CSS variable
// (Phases 0.5–1c), so `.dark` in tcs.css re-declares the channels and 2,900
// call sites follow. That is the entire mechanism.
//
// PERSISTENCE mirrors useFacilityScope: localStorage, keyed PER USER, so
// switching accounts on a shared machine does not inherit someone else's
// theme. There is one wrinkle useFacilityScope does not have — see LAST_KEY.
// ============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './auth';
import { token } from './design-tokens';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_PREFIX = 'tcs.theme.';

// The pre-paint script in index.html runs BEFORE React, before Firebase, and
// therefore before we know who the user is — so it cannot read a per-user key.
// It reads this one instead: the last theme actually applied on this device.
// Without it the app paints light and then snaps to dark once auth resolves,
// which is the flash this key exists to prevent. It is a device-level cache of
// a user-level preference, and the per-user key remains the source of truth:
// if a different person signs in, their own key wins on the first render.
const LAST_KEY = 'tcs.theme.last';

const DEFAULT_CHOICE: ThemeChoice = 'light'; // Ben's decision, 2026-08-11

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark';
}

function storageKey(employeeId: string | null | undefined): string | null {
  return employeeId ? `${STORAGE_PREFIX}${employeeId}` : null;
}

function readStored(employeeId: string | null | undefined): ThemeChoice | null {
  const key = storageKey(employeeId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return isChoice(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStored(employeeId: string | null | undefined, choice: ThemeChoice): void {
  const key = storageKey(employeeId);
  try {
    if (key) window.localStorage.setItem(key, choice);
    // Always update the device cache, even for a signed-out user, so the
    // pre-paint script has something to go on next load.
    window.localStorage.setItem(LAST_KEY, choice);
  } catch {
    /* private browsing / quota — the theme just falls back to the default */
  }
}

export function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** Pure, so it can be tested without a renderer or a media query. */
export function resolveTheme(choice: ThemeChoice, systemDark: boolean): ResolvedTheme {
  if (choice === 'system') return systemDark ? 'dark' : 'light';
  return choice;
}

// The <meta name="theme-color"> drives the browser/OS chrome around the page
// (the iOS status bar, the Android address bar). Left alone it stays at the
// light value and an AM on a phone gets a near-black page under a white bar.
//
// The value is READ FROM THE TOKENS rather than written here: a second copy of
// a colour is how the app came to have four disagreeing palettes in the first
// place, and the design gate correctly rejected the first version of this file
// for holding two hexes. Order matters — toggle the class, then read, because
// token() re-reads whenever the <html> className changes.
//
// The pre-paint script in index.html is the ONE place that must hard-code a
// value, because no stylesheet has loaded when it runs. It is commented there.
function apply(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const chrome = token(resolved === 'dark' ? '--tcs-bg-muted' : '--tcs-text');
  if (chrome.startsWith('#')) meta.setAttribute('content', chrome);
}

type ThemeContextValue = {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { employee } = useAuth();
  const employeeId = employee?.employee_id ?? null;

  const [choice, setChoiceState] = useState<ThemeChoice>(() => {
    try {
      const raw = window.localStorage.getItem(LAST_KEY);
      return isChoice(raw) ? raw : DEFAULT_CHOICE;
    } catch {
      return DEFAULT_CHOICE;
    }
  });
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Once we know WHO is signed in, their own stored choice takes over from the
  // device cache. If they have never chosen, fall back to the default rather
  // than inheriting whatever the previous person on this machine picked.
  useEffect(() => {
    if (!employeeId) return;
    const stored = readStored(employeeId);
    setChoiceState(stored ?? DEFAULT_CHOICE);
  }, [employeeId]);

  // Follow the OS only while the choice is 'system'. The listener is cheap and
  // always attached; resolveTheme ignores systemDark for the other two.
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved = resolveTheme(choice, systemDark);

  useEffect(() => {
    apply(resolved);
  }, [resolved]);

  const setChoice = useCallback(
    (c: ThemeChoice) => {
      setChoiceState(c);
      writeStored(employeeId, c);
    },
    [employeeId],
  );

  const value = useMemo(() => ({ choice, resolved, setChoice }), [choice, resolved, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

// ============================================================================
// ROLLOUT — OPEN TO EVERYONE as of 2026-08-12 (Ben: "make it available for
// everyone", after reviewing it on his own account).
//
// It was gated to a single employee id for one day. The allowlist is kept, and
// kept dead, on purpose: it is the revert path. Setting ROLLOUT_ALL back to
// false narrows this to the listed ids in one line, with no migration and no
// deploy dependency beyond the app itself.
//
// Note what this gate does and does not do. It hides the Appearance CONTROL.
// The theme machinery above always runs, so the default — Light — is what
// everyone who never opens the menu sees, exactly as before. Turning the gate
// off does not strand anyone in dark: their stored choice stops being
// reachable, and the resolved theme falls back to the default on next load.
//
// It stays a compile-time constant rather than a DB flag deliberately. A flag
// read over the network can return a plausible EMPTY result before auth lands,
// which is precisely how the notification badge read a truthful zero for weeks
// (see the 2026-08-10 handover entry). There is nothing to be gained here by
// making a colour scheme depend on a query.
// ============================================================================
const ROLLOUT_ALL = true;
const ROLLOUT_EIDS = new Set(['EID-0015']); // the revert path — see above

/**
 * Pure, so BOTH modes stay under test even though the config above is a
 * compile-time constant. The narrowed mode must fail CLOSED on a missing id —
 * that is the property worth pinning, because it is the one that breaks
 * silently if someone reverses the condition.
 */
export function isInRollout(
  employeeId: string | null | undefined,
  cfg: { all: boolean; eids: Set<string> },
): boolean {
  if (cfg.all) return true;
  return !!employeeId && cfg.eids.has(employeeId);
}

export function canChooseTheme(employeeId: string | null | undefined): boolean {
  return isInRollout(employeeId, { all: ROLLOUT_ALL, eids: ROLLOUT_EIDS });
}
